import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const getDefaultState = () => {
  return {
    itemTransactions: [],
    itemTransactionCount: 0,
    consumedItems: [],
    consumedItemsCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an itemTransaction
 * used for all queries which return an itemTransaction
 * @type {DocumentNode}
 */
const ITEM_TRANSACTION_REQUEST_FIELDS = gql`
  fragment itemTransactionFields on ItemTransaction {
    _id
    createdAt
    updatedAt
    quantity
    type
    createdBy {
      _id
      firstname
      lastname
      email
    }
    item {
      number
      description
    }
    fromProject {
      number
      customer {
        lastname
        firstname
      }
    }
    toProject {
      number
      customer {
        lastname
        firstname
      }
    }
    fromStorage {
      number
      name
      itemStorageType
    }
    toStorage {
      number
      name
      itemStorageType
    }
  }
`;

/**
 * Defines the fields which are requested for a consumed item
 * used for all queries which return consumed items
 * @type {DocumentNode}
 */
const CONSUMED_ITEM_REQUEST_FIELDS = gql`
  fragment consumedItemFields on ConsumedItemByProject {
    project {
      id
      number
      customer {
        lastname
        firstname
      }
    }
    item {
      _id
      number
      description
    }
    totalQuantityConsumed
  }
`;

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered itemTransaction list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchItemTransactionsPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query itemTransactionsPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: ItemTransactionFilterInput
          ) {
            itemTransactionsPaginated(
              pagination: $pagination
              sorting: $sorting
              filters: $filters
            ) {
              itemTransactions {
                ...itemTransactionFields
              }
              itemTransactionCount
            }
          }
          ${ITEM_TRANSACTION_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_ITEM_TRANSACTIONS', response.data.itemTransactionsPaginated.itemTransactions);
      commit(
        'SET_ITEM_TRANSACTION_COUNT',
        response.data.itemTransactionsPaginated.itemTransactionCount
      );
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },
  /**
   * Fetch a paginated, sorted and filtered consumed items list for a project through the block
   * project-consumed-items. The server forces the list to the project.
   * @param {*} param0
   * @param {*} payload
   */
  async fetchConsumedItemsByProjectPaginated(
    { commit, dispatch },
    { projectNumber, pagination, sorting, filters }
  ) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectConsumedItems(
            $projectNumber: Int!
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: ConsumedItemsFilterInput
          ) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              blocks {
                consumedItems {
                  id
                  number
                  consumedItems(pagination: $pagination, sorting: $sorting, filters: $filters) {
                    consumedItems {
                      ...consumedItemFields
                    }
                    consumedItemsCount
                  }
                }
              }
            }
          }
          ${CONSUMED_ITEM_REQUEST_FIELDS}
        `,
        variables: {
          projectNumber: projectNumber,
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      const consumedItemsPages = response.data.projectByNumber.blocks.consumedItems.consumedItems;
      commit('SET_CONSUMED_ITEMS', consumedItemsPages.consumedItems);
      commit('SET_CONSUMED_ITEMS_COUNT', consumedItemsPages.consumedItemsCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ITEM_TRANSACTIONS: (state, itemTransactions) =>
    itemTransactions ? (state.itemTransactions = itemTransactions) : (state.itemTransactions = []),
  SET_ITEM_TRANSACTION_COUNT: (state, itemTransactionCount) =>
    (state.itemTransactionCount = itemTransactionCount),
  SET_CONSUMED_ITEMS: (state, consumedItems) =>
    consumedItems ? (state.consumedItems = consumedItems) : (state.consumedItems = []),
  SET_CONSUMED_ITEMS_COUNT: (state, consumedItemsCount) =>
    (state.consumedItemsCount = consumedItemsCount),
};

/**
 * Getters
 */
const getters = {
  getItemTransactions: (state) => state.itemTransactions,
  getItemTransaction: (state) => (itemTransactionId) =>
    state.itemTransactions.find((itemTransaction) => itemTransaction._id == itemTransactionId),
  getItemTransactionCount: (state) => state.itemTransactionCount,
  getConsumedItems: (state) => state.consumedItems,
  getConsumedItemsCount: (state) => state.consumedItemsCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
